import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import type { ThemeTokens } from '../../lib/timerThemes';
import { observeResize } from '../../lib/observeResize';

// Fit the complete name inside its own cell, without shifting the centered title.
export function FittedText({
  text,
  size,
  color,
  brand = false,
  className = '',
  style,
  measurementText,
}: {
  text: string;
  size: number;
  color: string;
  brand?: boolean;
  className?: string;
  style?: CSSProperties;
  measurementText?: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const label = useRef<HTMLSpanElement>(null);
  const sample = useRef<HTMLSpanElement>(null);
  const measuredText = measurementText ?? text;
  useLayoutEffect(() => {
    const box = container.current;
    const span = label.current;
    if (!box || !span) return;
    const fit = () => {
      span.style.fontSize = `${size}px`;
      const measured = sample.current ?? span;
      measured.style.fontSize = `${size}px`;
      const scale = Math.min(
        1,
        box.clientWidth / Math.max(1, measured.scrollWidth),
        box.clientHeight / Math.max(1, measured.scrollHeight),
      );
      span.style.fontSize = `${size * scale}px`;
    };
    const stop = observeResize(box, fit);
    let active = true;
    document.fonts?.ready.then(() => {
      if (active) fit();
    });
    return () => {
      active = false;
      stop();
    };
  }, [measuredText, size, brand, className, style?.fontFamily, style?.letterSpacing]);
  return (
    <div
      ref={container}
      className="relative min-w-0 flex-1 min-h-0 w-full h-full flex items-center justify-center overflow-hidden"
      title={text}
      data-fitted-text
    >
      {measurementText && (
        <span
          ref={sample}
          aria-hidden="true"
          className={`absolute invisible whitespace-nowrap font-bold leading-tight ${className}`}
          style={{
            ...style,
            fontSize: size,
            position: 'absolute',
            width: 'max-content',
            left: 0,
            top: 0,
          }}
        >
          {measurementText}
        </span>
      )}
      <span
        ref={label}
        className={`block shrink-0 whitespace-nowrap font-bold leading-tight ${brand ? 'uppercase tracking-widest' : ''} ${className}`}
        style={{ ...style, fontSize: size, color }}
      >
        {text}
      </span>
    </div>
  );
}

export function TimerHeader({
  titleOnly = false,
  separateTitle,
  logoUrl,
  siteName,
  name,
  theme,
  fontSizes,
  scale,
}: {
  titleOnly?: boolean;
  separateTitle: boolean;
  logoUrl: string | null;
  siteName: string;
  name: string;
  theme: ThemeTokens;
  fontSizes: Record<string, number>;
  scale: number;
}) {
  const sf = (px: number) => Math.round(px * scale);
  const title = (
    <FittedText text={name} size={sf(fontSizes.logoName ?? 22)} color={theme.statValueColor} />
  );
  return (
    <div
      className="flex items-center gap-3 h-full w-full min-w-0 overflow-hidden px-3"
      style={{ backgroundColor: theme.widgetBg }}
    >
      {titleOnly ? (
        title
      ) : (
        <>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              className="object-contain shrink-0"
              style={{ maxWidth: '25%', maxHeight: '90%', height: sf(80) }}
            />
          ) : (
            <span
              className="material-symbols-outlined shrink-0"
              style={{ fontSize: sf(fontSizes.logo ?? 48), color: theme.primaryColor }}
            >
              style
            </span>
          )}
          <div
            className={`min-w-0 flex-1 h-full flex ${separateTitle || theme.logoAlign === 'right' ? 'items-center gap-3' : 'flex-col'}`}
          >
            <FittedText
              text={siteName}
              size={sf(fontSizes.logoLabel ?? 14)}
              color={theme.brandLabelColor ?? theme.primaryColor}
              brand
            />
            {!separateTitle && title}
          </div>
        </>
      )}
    </div>
  );
}
